/* eslint-disable new-cap */
/* eslint-disable no-inline-comments */
/* eslint-disable line-comment-position */
import { BlueprintInstanceType } from '../shared.enums';
import { panelTrimData } from './panel-trim.data';
import { PanelTrimType } from './enums';
import { Publish } from '../../decorators';

import { Axis, RenderDetails, Vertex3 } from '../../spatial-classes';
import type { FlatTrimDimensions, PanelTrimData, PanelTrimDataIn, PanelTrimPanels } from './types';
import { getFlatTrimDimensionsCfn, getPairedSeamTrimCfn, getRenderingDetailsCfn, setTrimTypePairedCfn } from './functions';
import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';

export class PanelTrim extends KitchenDesignBase<PanelTrimDataIn> {
	#trimType: PanelTrimType;
	#panels: PanelTrimPanels;

	constructor (options:KitchenDesignOptions<PanelTrimDataIn> = {}) {
		super(BlueprintInstanceType.PanelTrim, options);

		const { dataIn = null } = options;
		const data = panelTrimData(dataIn);

		this._id = data.id;
		this.#trimType = data.trimType;
		this.#panels = data.panels;

		this.setInitialized();
	}

	/**
	 * Sets the trim profile used for this corner
	 * @emits panel_trim.updated
	 */
	@Publish('panel_trim', 'updated')
	set trimType (trimType: PanelTrimType) { this.#trimType = trimType; }
	get trimType (): PanelTrimType { return this.#trimType }

	get panels (): PanelTrimPanels { return this.#panels }

	/**
	 * true when the trim is the 3" flat strip spanning an island seam rather than an L-profile corner
	 */
	get isFlat (): boolean { return this.#trimType === PanelTrimType.straight }

	/**
	 * panel trim center represents the position relative to the parent island
	 * @returns Vertex3 representing the center position relative to the parent island
	 */
	get center (): Vertex3 {
		if (this.refMap.island.panels && this.panels.length > 1) {
			const panel0 = this.refMap.island.panels.findById(this.panels[0]);

			if (panel0) {
				const { corners, center } = panel0.getSidePoints({ translate: true, rotate: true });
				const corner = panel0.connections.left.targetPanelId === this.panels[1]
					? corners[0] // left point
					: corners[1]; // right point

				return new Vertex3({ dataIn: { x: corner.x, y: center.y, z: corner.z }});
			}
		}

		return new Vertex3();
	}

	/**
	 * panel trim axis represents the rotation/orientation of the panel trim
	 * @returns Axis representing the rotation
	 */
	get axis (): Axis {
		let angle = 0;

		if (this.refMap.island.panels && this.panels.length > 1) {
			const panel0 = this.refMap.island.panels.findById(this.panels[0]);
			angle = panel0?.getOriginAngle() ?? 0;
		}

		return new Axis({ dataIn: { x: 0, y: angle, z: 0 }});
	}

	/**
	 * panel trim origin represents the world space position of the panel trim
	 * origin = parentIsland.origin + this.center
	 * @returns Vertex3 representing the world space position
	 */
	get origin (): Vertex3 {
		const origin = new Vertex3();
		if (this.refMap.island) {
			origin.moveTo(this.refMap.island.origin);
			origin.add(this.center);
		}
		return origin;
	}

	get renderingDetails (): RenderDetails { return getRenderingDetailsCfn(this) }

	/**
	 * derived width/height pair for the flat trim strip, split into a near and far half
	 * @returns FlatTrimDimensions
	 */
	get flatTrimDimensions (): FlatTrimDimensions { return getFlatTrimDimensionsCfn(this) }

	/**
	 * the trim on the adjoining island that bridges the same seam as this corner, or null for a free corner
	 */
	get pairedSeamTrim (): PanelTrim | null { return getPairedSeamTrimCfn(this) }

	/**
	 * Sets the trim type for this corner, also converting the counterpart trim on the adjoining
	 * island if this corner's seam has one (a free corner just converts itself)
	 * @param trimType the trim profile to apply
	 */
	public setTrimTypePaired (trimType: PanelTrimType): void { setTrimTypePairedCfn(this, trimType); }

	public serialize (): PanelTrimData {
		return {
			id: this._id,
			trimType: this.#trimType,
			panels: this.#panels
		};
	}
}
